import React from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { CallRecordKey, TicketKey } from 'flow/types/EntityKeys';
import { OmniCallEventKey } from 'flow/types/TicketEventData';
import { selectCallRecordFromKey } from 'flow/selectors/call-records-selectors';
import { selectEventCallRecordKey } from 'flow/selectors/TicketEventSelectors';
import { useCallRecordExtendedTranscription } from 'flow/hooks/api/useCallRecordExtendedTranscription';
import SectionCallType from 'flow/components/pbx/v2/detail-page/call-event/SectionCallType';
import SectionCallDetails from 'flow/components/pbx/v2/detail-page/tabs/SectionCallDetails';
import SectionRecordingAndTranscription from 'flow/components/call-history-v2/transcription/SectionRecordingAndTranscription';
import SectionCounterpartVerification from 'flow/components/pbx/v2/pbx-queue-items/history/call-record/SectionCounterpartVerification';

interface Props {
    eventKey: OmniCallEventKey;
    ticketKey: TicketKey;
}

/**
 * Renders one consolidated call event. The event stores only its `callRecordKey`; this resolves it
 * from the ticket-events store, then renders the card from the canonical call record once it's
 * available (see {@link CallRecordCard}).
 */
const EventCallCard = ({ eventKey, ticketKey }: Props) => {
    const callRecordKey = useAppSelector((state) =>
        selectEventCallRecordKey(state, ticketKey, eventKey),
    );

    if (!callRecordKey) return null;

    return (
        <CallRecordCard callRecordKey={callRecordKey} ticketKey={ticketKey} />
    );
};

interface CallRecordCardProps {
    callRecordKey: CallRecordKey;
    ticketKey: TicketKey;
}

/**
 * The card body, driven by the call record that was upserted into the canonical call-records store on
 * ingest. Reuses the same section components as the call record page's log entry, so the ticket card
 * and the call record page render identically; the only tickets-specific addition is the summary's
 * "…" menu (suggest tags, copy summary).
 */
const CallRecordCard = ({ callRecordKey, ticketKey }: CallRecordCardProps) => {
    const callRecord = useAppSelector((state) =>
        selectCallRecordFromKey(state, callRecordKey),
    );

    const extendedTranscription =
        useCallRecordExtendedTranscription(callRecordKey);

    if (!callRecord) return null;

    return (
        <div className="flex flex-col gap-1 py-3">
            <SectionCallType callRecordKey={callRecordKey} pageType="tickets" />
            <SectionCallDetails
                callRecordKey={callRecordKey}
                pageType="tickets"
            />
            <SectionRecordingAndTranscription
                callRecordKey={callRecordKey}
                extendedTranscription={extendedTranscription}
                // Unconditional, unlike the PBX page's `pageType === 'pbx'`: personal recordings
                // and transcriptions are never shown in a ticket, by product decision, not even to
                // the agent who owns them. A ticket is shared workspace, so anything on the card is
                // effectively addressed to the team.
                hidePersonalContent={true}
                pageType="tickets"
                ticketKey={ticketKey}
            />
            <SectionCounterpartVerification callRecordKey={callRecordKey} />
        </div>
    );
};

export default React.memo(EventCallCard);
